import { notifyError } from 'config/setupBugsnag';

import {
  IteroProposalServerResponse,
  IteroProposalDetailsServerResponse,
  IteroProposalClient,
  IteroProposaListlClient,
  IteroProposalServerData,
} from 'types';
import RestUtility from '../../../restUtility';
import { iteroEndpoint } from '../../../restUtility/restEndpoints';
import getConfig from 'next/config';

export class IteroProposalService {
  private restUtility: RestUtility;

  constructor() {
    this.restUtility = new RestUtility();
  }

  shouldFetchIteroProposals() {
    const { publicRuntimeConfig } = getConfig();
    const storeRegion = publicRuntimeConfig.STORE_REGION.toUpperCase();
    return storeRegion === 'NA' || storeRegion === 'EMEA';
  }

  async getApprovedIteroProposals() {
    if (!this.shouldFetchIteroProposals()) {
      return {
        iteroProposals: [],
        showNotification: false,
      };
    }
    try {
      const response = await this.restUtility.get<IteroProposalServerResponse>(
        `${iteroEndpoint}/proposals`
      );

      return this.buildProposalsForClient(response.data);
    } catch (error) {
      notifyError('ApiError:: getApprovedIteroProposals', error);
      throw error;
    }
  }

  async sendProposalViewedNotification(proposalId: string) {
    try {
      await this.restUtility.patch(
        `${iteroEndpoint}/proposal_notification?proposal_id=${proposalId}`
      );
    } catch (error) {
      notifyError(
        `ApiError:: sendProposalViewedNotification, ${proposalId}`,
        error
      );
      throw error;
    }
  }

  buildProposalsForClient(
    proposalServerResponse: IteroProposalServerData
  ): IteroProposaListlClient {
    const iteroProposals = proposalServerResponse.proposals.map(proposal => {
      return this.buildProposalForClient(proposal);
    });
    const showNotification = proposalServerResponse.show_notification;
    return {
      iteroProposals,
      showNotification,
    };
  }

  buildProposalForClient(
    response: IteroProposalDetailsServerResponse
  ): IteroProposalClient {
    const proposalDetails = response.proposal;
    const proposalLineItemDetails = response.line_items;

    return {
      proposalId: proposalDetails.sfid,
      proposalName: proposalDetails.proposal_name,
      displayTotal: proposalDetails.display_total,
      displayAdjustmentTotal: proposalDetails.display_adjustment_total,
      displayTotalAfterAdjustments:
        proposalDetails.display_total_after_adjustments,
      billAddressId: proposalDetails.bill_address_id,
      shipAddressId: proposalDetails.ship_address_id,
      scannerFundingSource: proposalDetails.funding_source,
      scannerSubFundingSource: proposalDetails.sub_funding_source,
      financingCompany: proposalDetails.financing_company,
      existingScannerSerialNumber: proposalDetails.serial_number,
      customerNeed: proposalDetails.customer_need,
      processType: proposalDetails.process_type,
      promotion: proposalDetails.promotion,
      saleType: proposalDetails.sale_type,
      estimatedMonthlyPayment: proposalDetails.estimated_monthly_payment,
      entityName: proposalDetails.entity_name,
      tradeInSerialNumber: proposalDetails.trade_in_serial_number, // for EMEA proposals, in Trade-In Details section of the terms and conditions modal, NOT THE SAME AS existingScannerSerialNumber which is shown in the Promotion Specific Details section of the terms and conditions modal
      opportunityId: proposalDetails.opportunity_id,
      opportunityOwnerName: proposalDetails.opportunity_owner_name,
      opportunityNumber: proposalDetails.opportunity_number,
      scannerModel: proposalDetails.scanner_model,
      servicePlanDiscount: proposalDetails.service_plan_discount,
      lineitems: proposalLineItemDetails.map(lineItem => {
        return {
          name: lineItem.name,
          quantity: lineItem.quantity,
          displayItemUnitPrice: lineItem.list_price,
          displayItemTotal: lineItem.net_price,
          promotion: {
            programName: lineItem.promotion?.program_name,
            programCode: lineItem.promotion?.program_code,
            amount: lineItem.promotion?.promotion_amount,
          },
          softwareType: lineItem.scanner_type,
          serviceTerm: lineItem.service_type,
          servicePlan: lineItem.service_plan,
          sellingTerm: lineItem.selling_term,
          description: lineItem.description,
          itemSequence: lineItem.item_sequence,
          netUnitPrice: lineItem.net_unit_price,
          sku: lineItem.sku,
          options: lineItem?.options
            ?.filter(option => !option.service_type || !option.service_plan)
            .map(option => {
              return {
                name: option.name,
                quantity: option.quantity,
                displayItemUnitPrice: option.list_price,
                displayItemTotal: option.net_price,
              };
            }),
          subscriptions: lineItem?.options
            ?.filter(option => option.service_type || option.service_plan)
            .map(option => {
              return {
                name: option.name,
                quantity: option.quantity,
                displayItemUnitPrice: option.list_price,
                displayItemTotal: option.net_price,
                softwareType: option?.scanner_type,
                serviceTerm: option?.service_type,
                servicePlan: option?.service_plan,
                promotionCode: option?.promotion_code,
                adjustmentAmount: option?.adjustment_amount,
              };
            }),
        };
      }),
    };
  }
}
